/**
 * offerPrefs — local "do not show this offer" preferences.
 *
 * The offer popup carries a "do not show this offer" checkbox. Ticking it hides
 * that offer from the STARTUP popup only — the StoreScreen OFFERS tab still
 * lists every live offer, so a dismissed deal is never lost, just not pushed in
 * the player's face on launch.
 *
 * Stored in localStorage as a JSON array of offer ids. The webview origin is the
 * CDN domain (app.streetdicelegends.com), so this persists across OTA updates.
 */

const STORAGE_KEY = 'sdl.offers.hidden';
const LAUNCH_KEY = 'sdl.offers.launches';
const LAST_SHOWN_KEY = 'sdl.offers.popupLastShown';

/** Startup-popup gating (tunable). The popup is a courtesy nudge, not a hard
 *  sell — show it only once the player is invested, and never more than once a
 *  day. The Store OFFERS tab is always available regardless of these. */
const MIN_LAUNCHES_FOR_POPUP = 3;          // earn the moment: 3rd app open onward
const POPUP_COOLDOWN_MS = 24 * 60 * 60 * 1000; // at most once per 24h

function load(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((x) => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function save(ids: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* private mode / quota — non-fatal, just won't persist */
  }
}

/** Offer ids the player chose to hide from the startup popup. */
export function getHiddenOfferIds(): Set<string> {
  return load();
}

export function isOfferHidden(id: string): boolean {
  return load().has(id);
}

export function setOfferHidden(id: string, hidden: boolean): void {
  const ids = load();
  if (hidden) ids.add(id);
  else ids.delete(id);
  save(ids);
}

// ── Startup-popup frequency gate ────────────────────────────────────────────

function readNum(key: string): number {
  const n = Number(localStorage.getItem(key));
  return Number.isFinite(n) ? n : 0;
}

/** Bump the app-launch counter (call once per cold start). Returns the new count. */
export function recordAppLaunch(): number {
  const next = readNum(LAUNCH_KEY) + 1;
  try { localStorage.setItem(LAUNCH_KEY, String(next)); } catch { /* private mode */ }
  return next;
}

/**
 * Whether the startup offer popup may show right now: the player has opened the
 * app enough times (invested) AND it hasn't shown within the cooldown window.
 */
export function canShowStartupPopup(now: number): boolean {
  if (readNum(LAUNCH_KEY) < MIN_LAUNCHES_FOR_POPUP) return false;
  return now - readNum(LAST_SHOWN_KEY) >= POPUP_COOLDOWN_MS;
}

/** Stamp the popup as shown now (starts the once-per-day cooldown). */
export function markStartupPopupShown(now: number): void {
  try { localStorage.setItem(LAST_SHOWN_KEY, String(now)); } catch { /* private mode */ }
}
